import { parseGreetingCardMessageLimit } from '../utils/greetingCardMessageLimit'

type BuilderFeatures = {
  magicWriterEnabled: boolean
  videoGiftMessageEnabled: boolean
  voiceNoteEnabled: boolean
  imageUploadEnabled: boolean
  stickersEnabled: boolean
  videoGiftMessagePlacement: InteractiveElementPlacement
  voiceNotePlacement: InteractiveElementPlacement
  imageUploadPlacement: InteractiveElementPlacement
  stickerPlacement: InteractiveElementPlacement
  messageCharacterLimit: number
  allowTextStyleEditing: boolean
  insideTextFontMode: 'brand_only' | 'shopper_choice'
  useBrandFontForUi: boolean
  defaultInsideFontFamily?: string
}

export type InteractiveElementPlacement = 'none' | 'front' | 'inside' | 'both'

function parseBooleanFlag(value: unknown, fallback: boolean) {
  if (typeof value !== 'string') return fallback
  if (value === 'true') return true
  if (value === 'false') return false
  return fallback
}

export function parseInteractiveElementPlacement(
  value: unknown,
  legacyEnabled: unknown,
): InteractiveElementPlacement {
  if (
    value === 'none' ||
    value === 'front' ||
    value === 'inside' ||
    value === 'both'
  ) {
    return value
  }
  return parseBooleanFlag(legacyEnabled, true) ? 'both' : 'none'
}

export function parseBuilderDecorationFeatureFlags(
  query: Record<string, unknown>,
) {
  return {
    imageUploadPlacement: parseInteractiveElementPlacement(
      query.imageUploadPlacement,
      query.enableImageUpload,
    ),
    stickerPlacement: parseInteractiveElementPlacement(
      query.stickerPlacement,
      query.enableStickers,
    ),
  }
}

function parseStringValue(value: unknown) {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function parseInsideTextFontMode(
  value: unknown,
): 'brand_only' | 'shopper_choice' {
  return value === 'shopper_choice' ? 'shopper_choice' : 'brand_only'
}

export function useBuilderFeatures() {
  const route = useRoute()
  const { state } = useYuzuBuilder()
  const companyShopper = computed(() =>
    route.path?.startsWith('/v2/') ? state.value?.companyShopper : undefined,
  )

  const magicWriterEnabled = computed(() =>
    parseBooleanFlag(route.query.enableMagicWriter, true),
  )

  const videoGiftMessagePlacement = computed(() =>
    companyShopper.value
      ? 'none'
      : parseInteractiveElementPlacement(
          route.query.videoGiftMessagePlacement,
          route.query.enableVideoGiftMessage,
        ),
  )

  const voiceNotePlacement = computed(() =>
    companyShopper.value
      ? 'none'
      : parseInteractiveElementPlacement(
          route.query.voiceNotePlacement,
          route.query.enableVoiceNote,
        ),
  )

  const decorationFeatureFlags = computed(() =>
    parseBuilderDecorationFeatureFlags(route.query),
  )

  const imageUploadPlacement = computed(() =>
    companyShopper.value
      ? 'none'
      : decorationFeatureFlags.value.imageUploadPlacement,
  )

  const stickerPlacement = computed(() =>
    companyShopper.value
      ? 'none'
      : decorationFeatureFlags.value.stickerPlacement,
  )

  const videoGiftMessageEnabled = computed(
    () => videoGiftMessagePlacement.value !== 'none',
  )
  const voiceNoteEnabled = computed(() => voiceNotePlacement.value !== 'none')
  const imageUploadEnabled = computed(
    () => imageUploadPlacement.value !== 'none',
  )
  const stickersEnabled = computed(() => stickerPlacement.value !== 'none')

  const messageCharacterLimit = computed(
    () =>
      companyShopper.value?.messageCharacterLimit ??
      parseGreetingCardMessageLimit(route.query.messageCharacterLimit),
  )

  const allowTextStyleEditing = computed(
    () =>
      companyShopper.value?.allowTextStyleEditing ??
      parseBooleanFlag(route.query.allowTextStyleEditing, true),
  )

  const insideTextFontMode = computed(() =>
    companyShopper.value
      ? companyShopper.value.allowFontSelection
        ? 'shopper_choice'
        : 'brand_only'
      : parseInsideTextFontMode(route.query.insideTextFontMode),
  )

  const useBrandFontForUi = computed(() =>
    parseBooleanFlag(route.query.useBrandFontForUi, true),
  )

  const defaultInsideFontFamily = computed(
    () =>
      parseStringValue(route.query.defaultInsideFontFamily) ||
      parseStringValue(route.query.brandFontFamily),
  )

  const features = computed<BuilderFeatures>(() => ({
    magicWriterEnabled: magicWriterEnabled.value,
    videoGiftMessageEnabled: videoGiftMessageEnabled.value,
    voiceNoteEnabled: voiceNoteEnabled.value,
    imageUploadEnabled: imageUploadEnabled.value,
    stickersEnabled: stickersEnabled.value,
    videoGiftMessagePlacement: videoGiftMessagePlacement.value,
    voiceNotePlacement: voiceNotePlacement.value,
    imageUploadPlacement: imageUploadPlacement.value,
    stickerPlacement: stickerPlacement.value,
    messageCharacterLimit: messageCharacterLimit.value,
    allowTextStyleEditing: allowTextStyleEditing.value,
    insideTextFontMode: insideTextFontMode.value,
    useBrandFontForUi: useBrandFontForUi.value,
    defaultInsideFontFamily: defaultInsideFontFamily.value,
  }))

  return {
    features,
    magicWriterEnabled,
    videoGiftMessageEnabled,
    voiceNoteEnabled,
    imageUploadEnabled,
    stickersEnabled,
    videoGiftMessagePlacement,
    voiceNotePlacement,
    imageUploadPlacement,
    stickerPlacement,
    messageCharacterLimit,
    allowTextStyleEditing,
    insideTextFontMode,
    useBrandFontForUi,
    defaultInsideFontFamily,
  }
}
